export function useAccountancy() {
  const { openToast, openConfirmAlert } = useUserMessages();
  const { t } = useEkI18n();
  const { getIsDateInValidFinancialPeriod, getDatesFinancialPeriodStatus, getDatesFinancialAndSalaryPeriodStatus } = useAccountancyAPI();
  
  async function IsDateWithinValidFinancialPeriodValidationFlow(dateToCheck: string | null | undefined, accountSettingActionPolicy: ActionPolicy, confirmDialogMessage: string, blockToastMessage: string) {
    if (accountSettingActionPolicy == ActionPolicy.Allow) return true;

    try {
      const isValid = await getIsDateInValidFinancialPeriod(dateToCheck ?? new Date().toString());

      if (isValid) return true;

      if (accountSettingActionPolicy === ActionPolicy.Warn) {
        const confirmed = await openConfirmAlert(t('alert.warning'), confirmDialogMessage, t('buttons.continue'));

        return confirmed;
      }

      if (accountSettingActionPolicy === ActionPolicy.Block) {
        openToast(blockToastMessage, ToastType.ERROR);

        return false;
      }
    } catch {
      openToast(t('error.generic'), ToastType.ERROR);
    }

    return false;
  }
  
  async function getFinancialPeriodNote(newDates: Date[], accountSettingActionPolicy: ActionPolicy) : Promise<NoteReturnModel | null> {
    const datesWithFinancialPeriodStatus = await getDatesFinancialPeriodStatus(newDates);

    if (datesWithFinancialPeriodStatus === undefined || (Array.isArray(datesWithFinancialPeriodStatus?.Data) && datesWithFinancialPeriodStatus.Data.length === 0)) {
      return null;
    }

    const datesWithLockedFinancialPeriods = Object.entries(datesWithFinancialPeriodStatus.Data)
      .filter(([, value]) => value.IsClosed === true);

    const datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole = Object.entries(datesWithFinancialPeriodStatus.Data)
      .filter(([, value]) => value.IsClosed === false && value.IsOpenBecauseOfFinancialControllerRole === true);
  
    const totalSelectedDates = Object.keys(datesWithFinancialPeriodStatus.Data).length;

    const financialPeriodNote = await handleFinancialPeriodValidation(datesWithLockedFinancialPeriods, datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole, accountSettingActionPolicy, newDates, totalSelectedDates);

    return financialPeriodNote;
  }

  async function getFinancialAndSalaryPeriodNote(newDates: Date[], fitterId: number, financialPeriodsActionPolicy: ActionPolicy, salaryPeriodsActionPolicy: ActionPolicy) : Promise<NoteReturnModel[]> {
    const datesWithFinancialPeriodStatus = await getDatesFinancialAndSalaryPeriodStatus(newDates, fitterId);

    if (datesWithFinancialPeriodStatus === undefined || (Array.isArray(datesWithFinancialPeriodStatus?.Data) && datesWithFinancialPeriodStatus.Data.length === 0)) {
      return [];
    }

    const datesWithLockedFinancialPeriods = Object.entries(datesWithFinancialPeriodStatus.Data)
      .filter(([, value]) => value.IsClosed === true);

    const datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole = Object.entries(datesWithFinancialPeriodStatus.Data)
      .filter(([, value]) => value.IsClosed === false && value.IsOpenBecauseOfFinancialControllerRole === true);

    const datesWithClosedSalaryPeriods = Object.entries(datesWithFinancialPeriodStatus.Data)
      .filter(([, value]) => value.IsSalaryPeriodClosed === true);
  
    const totalSelectedDates = Object.keys(datesWithFinancialPeriodStatus.Data).length;

    const financialPeriodNote = await handleFinancialPeriodValidation(datesWithLockedFinancialPeriods, datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole, financialPeriodsActionPolicy, newDates, totalSelectedDates);
    const salaryPeriodNote = await handleSalaryPeriodValidation(datesWithClosedSalaryPeriods, salaryPeriodsActionPolicy, newDates, totalSelectedDates);

    const notes: NoteReturnModel[] = [financialPeriodNote, salaryPeriodNote].filter((note): note is NoteReturnModel => note !== null);

    return notes;
  }

  async function handleSalaryPeriodValidation(datesWithClosedSalaryPeriods : [string, DateFinancialAndSalaryPeriodStatus][], salaryPeriodsActionPolicy : ActionPolicy, newDates: Date[], totalSelectedDates: number) : Promise<NoteReturnModel | null> {
    if (datesWithClosedSalaryPeriods.length === totalSelectedDates && totalSelectedDates != 1) {
      return {
        note: t('hourRegistration.alert.salaryPeriodsAllAreClosed'),
        noteColor: getNoteColor(salaryPeriodsActionPolicy),
        blockAction: salaryPeriodsActionPolicy === ActionPolicy.Block
      };
    } else if (datesWithClosedSalaryPeriods.length > 0) {
      if (newDates.length === 1 && datesWithClosedSalaryPeriods.length === 1) { // If only one date is provided and only one closed salary period is returned
        return {
          note: t('hourRegistration.alert.salaryPeriodIsClosed'),
          noteColor: getNoteColor(salaryPeriodsActionPolicy),
          blockAction: salaryPeriodsActionPolicy === ActionPolicy.Block
        };
      } else {
        return {
          note: t('hourRegistration.alert.salaryPeriodsCountAreClosed', { 
            count: datesWithClosedSalaryPeriods.length 
          }),
          noteColor: getNoteColor(salaryPeriodsActionPolicy),
          blockAction: salaryPeriodsActionPolicy === ActionPolicy.Block
        };
      }
    }

    return null;
  }

  async function handleFinancialPeriodValidation(datesWithLockedFinancialPeriods: [string, DateFinancialPeriodStatus][], datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole: [string, DateFinancialPeriodStatus][], financialPeriodsActionPolicy : ActionPolicy, newDates : Date[], totalSelectedDates: number) : Promise<NoteReturnModel | null> {
    // Handle cases where both open (due to role) and locked periods exist
    if (datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole.length > 0 && datesWithLockedFinancialPeriods.length > 0) {
      const pluralCount = datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole.length > 1 || datesWithLockedFinancialPeriods.length > 1 ? 2 : 1;

      return {
        note: t('financialPeriodsValidation.noticeFinancialPeriodOpenBecauseOfFinancialControllerRoleAndBlocked', {
          count: pluralCount,
          countOpen: datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole.length,
          countBlocked: datesWithLockedFinancialPeriods.length
        }),
        noteColor: getNoteColor(financialPeriodsActionPolicy),
        blockAction: financialPeriodsActionPolicy === ActionPolicy.Block
      };
    } else if (datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole.length > 0) { // Handle cases with only open periods (due to financial controller role)
      if (newDates.length === 1 && datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole.length === 1) { // If only one date is provided and only one open period is returned
        return {
          note: t('financialPeriodsValidation.noticeFinancialPeriodOpenBecauseOfFinancialControllerRoleSingle'),
          noteColor: getNoteColor(ActionPolicy.Allow),
          blockAction: false
        };
      } else if (datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole.length === totalSelectedDates) {
        return {
          note: t('financialPeriodsValidation.noticeFinancialPeriodOpenBecauseOfFinancialControllerRoleAll'),
          noteColor: getNoteColor(ActionPolicy.Allow),
          blockAction: false
        };
      } else {
        return {
          note: t('financialPeriodsValidation.noticeFinancialPeriodOpenBecauseOfFinancialControllerRole', {
            count: datesWithOpenFinancialPeriodsBecauseOfFinancialControllerRole.length
          }),
          noteColor: getNoteColor(ActionPolicy.Allow),
          blockAction: false
        };
      }
    } else if (datesWithLockedFinancialPeriods.length > 0) { // Handle cases with only locked periods
      if (newDates.length === 1 && datesWithLockedFinancialPeriods.length === 1) { // If only one date is provided and only one locked period is returned
        return {
          note: t('financialPeriodsValidation.noticeFinancialPeriodBlockedSingle'),
          noteColor: getNoteColor(financialPeriodsActionPolicy),
          blockAction: financialPeriodsActionPolicy === ActionPolicy.Block
        };
      } else if (datesWithLockedFinancialPeriods.length === totalSelectedDates) {
        return {
          note: t('financialPeriodsValidation.noticeFinancialPeriodBlockedAll'),
          noteColor: getNoteColor(financialPeriodsActionPolicy),
          blockAction: financialPeriodsActionPolicy === ActionPolicy.Block
        };
      } else {
        return {
          note: t('financialPeriodsValidation.noticeFinancialPeriodBlocked', {
            count: datesWithLockedFinancialPeriods.length
          }),
          noteColor: getNoteColor(financialPeriodsActionPolicy),
          blockAction: financialPeriodsActionPolicy === ActionPolicy.Block
        };
      }
    } else {
      return null;
    }
  }

  function getNoteColor(actionPolicy: ActionPolicy): 'warning' | 'danger' | 'info' {
    switch (actionPolicy) {
      case ActionPolicy.Block:
        return 'danger';
      case ActionPolicy.Warn:
        return 'warning';
      default:
        return 'info';
    }
  }

  return {
    IsDateWithinValidFinancialPeriodValidationFlow,
    getFinancialPeriodNote,
    getFinancialAndSalaryPeriodNote
  };
}